'use client'
import Link from 'next/link'
import Image from 'next/image'
import { useState, useEffect } from 'react'
import { Menu, X, User, ShoppingBag, Lock, Sun, Moon, ChevronDown } from 'lucide-react'
import { useCart } from '@/context/CartContext'
import { useTheme } from '@/components/theme/ThemeProvider'
import { Button } from '@/components/ui/Button'

const NAV_LINKS = [
  { href: '/', label: 'Home' },
  { href: '/collections', label: 'Collections' },
  { href: '/about', label: 'About' },
  { href: '/contact', label: 'Contact' },
]

export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [customer, setCustomer] = useState<{ email: string; studioName: string; status?: string } | null>(null)
  const [isAdmin, setIsAdmin] = useState(false)
  const { totalItems } = useCart()
  const { theme, toggle } = useTheme()
  const [megaOpen, setMegaOpen] = useState(false)
  const [categories, setCategories] = useState<{ name: string; slug: string }[]>([])

  useEffect(() => {
    fetch('/api/categories?withProducts=1')
      .then(r => r.json())
      .then(d => setCategories(d.categories || []))
      .catch(() => {})
  }, [])

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    const loadCustomer = async () => {
      try {
        const res = await fetch('/api/customer/me')
        const d = res.ok ? await res.json() : null
        if (d && d.email) setCustomer(d)
      } catch { /* not logged in */ }
    }
    loadCustomer()
  }, [])

  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const res = await fetch('/api/admin/login')
        const d = res.ok ? await res.json() : null
        if (d?.admin) setIsAdmin(true)
      } catch { /* not admin */ }
    }
    checkAdmin()
  }, [])

  return (
    <header
      className={`sticky top-0 z-[100] transition-all duration-300 ${
        scrolled
          ? 'glass-elevated shadow-glass-elevated'
          : 'bg-orion-bg/95 backdrop-blur-md border-b border-orion-border/60'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-18">
          {/* Logo */}
          <Link href="/" className="flex items-center group flex-shrink-0">
            <Image
              src="/orion_logo.png"
              alt="Orion Body Jewellery"
              width={140}
              height={56}
              className="h-10 w-auto object-contain transition-opacity duration-200 group-hover:opacity-80"
              priority
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1 lg:gap-8">
            {NAV_LINKS.map((l) =>
              l.href === '/collections' ? (
                <div
                  key={l.href}
                  className="relative"
                  onMouseEnter={() => setMegaOpen(true)}
                  onMouseLeave={() => setMegaOpen(false)}
                >
                  <Link
                    href="/collections"
                    aria-expanded={megaOpen}
                    className={`flex items-center gap-1.5 text-xs font-medium uppercase tracking-[0.15em] transition-colors duration-200 ${
                      megaOpen ? 'text-orion-accent' : 'text-orion-muted hover:text-orion-accent'
                    }`}
                  >
                    {l.label}
                    <ChevronDown size={13} className={`transition-transform duration-200 ${megaOpen ? 'rotate-180' : ''}`} />
                  </Link>

                  {megaOpen && (
                    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 w-[min(56rem,calc(100vw-3rem))]">
                      <div className="rounded-2xl border border-orion-border/60 p-6 grid grid-cols-4 gap-6 animate-slide-down bg-orion-bg-elevated/95 backdrop-blur-xl shadow-glass-elevated">
                        <div className="col-span-3">
                          <p className="text-orion-muted text-[10px] font-semibold uppercase tracking-[0.2em] mb-3">
                            Shop by category
                          </p>
                          <div className="grid grid-cols-3 gap-x-4 gap-y-1">
                            {categories.length === 0 && (
                              <span className="text-orion-muted text-xs">Loading…</span>
                            )}
                            {categories.map(cat => (
                              <Link
                                key={cat.slug}
                                href={`/collections?cat=${encodeURIComponent(cat.slug)}`}
                                className="text-orion-text hover:text-orion-accent text-sm py-1.5 transition-colors truncate"
                              >
                                {cat.name}
                              </Link>
                            ))}
                          </div>
                        </div>
                        <div className="border-l border-orion-border/60 pl-6">
                          <p className="text-orion-muted text-[10px] font-semibold uppercase tracking-[0.2em] mb-3">
                            Refine
                          </p>
                          <div className="flex flex-col gap-1.5">
                            <Link href="/collections?materialGrade=ASTM+F136+Titanium" className="text-orion-text hover:text-orion-accent text-sm py-1 transition-colors">ASTM F136 Titanium</Link>
                            <Link href="/collections?threading=Threadless" className="text-orion-text hover:text-orion-accent text-sm py-1 transition-colors">Threadless</Link>
                            <Link href="/collections?stock=in" className="text-orion-text hover:text-orion-accent text-sm py-1 transition-colors">In stock only</Link>
                            <Link href="/collections" className="text-orion-accent hover:underline text-sm py-1 font-medium">View all →</Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={l.href}
                  href={l.href}
                  className="text-orion-muted hover:text-orion-accent transition-colors duration-200 text-xs font-medium uppercase tracking-[0.15em]"
                >
                  {l.label}
                </Link>
              )
            )}
          </nav>

          {/* Right side actions */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Wholesale CTA - always visible on desktop */}
            <Link href="/customer/signup" className="hidden sm:inline-flex">
              <Button variant="primary" size="sm" iconLeft={<Lock className="w-3.5 h-3.5" />}>
                Apply for Wholesale
              </Button>
            </Link>

            {/* Admin Login */}
            {isAdmin && (
            <Link
              href="/admin"
              className="hidden sm:flex items-center gap-1.5 text-orion-muted hover:text-orion-accent text-xs font-medium transition-colors duration-200 px-2 py-2 rounded-lg"
            >
              <Lock size={16} />
              <span>Admin</span>
            </Link>
            )}

            {/* Account / Sign In */}
            {customer ? (
              <Link
                href="/customer/dashboard"
                className="hidden sm:flex items-center gap-1.5 text-orion-text hover:text-orion-accent text-xs font-medium transition-colors duration-200 px-2 py-2 rounded-lg"
              >
                <User size={16} className="text-orion-accent" />
                <span className="max-w-[140px] truncate">{customer.studioName || 'Account'}</span>
              </Link>
            ) : (
              <Link
                href="/customer/login"
                className="hidden sm:flex items-center gap-1.5 text-orion-muted hover:text-orion-accent text-xs font-medium transition-colors duration-200 px-2 py-2 rounded-lg"
              >
                <User size={16} />
                <span>Sign In</span>
              </Link>
            )}

            {/* Light / dark toggle */}
            <button
              onClick={toggle}
              className="p-2 text-orion-muted hover:text-orion-accent transition-colors duration-200 rounded-lg"
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
            >
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            {/* Cart */}
            <Link
              href="/cart"
              className="relative p-2 text-orion-muted hover:text-orion-text transition-colors duration-200"
              aria-label={`Cart, ${totalItems} items`}
            >
              <ShoppingBag size={18} />
              {totalItems > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-orion-accent text-orion-bg text-[10px] font-bold leading-none">
                  {totalItems > 99 ? '99+' : totalItems}
                </span>
              )}
            </Link>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden text-orion-muted hover:text-orion-text transition-colors duration-200 p-2"
              aria-label="Menu"
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-orion-border/50 pb-4 animate-slide-down">
            <nav className="flex flex-col px-2 py-3 gap-1">
              {NAV_LINKS.map((l) => (
                <Link
                  key={l.href}
                  href={l.href}
                  onClick={() => setMobileOpen(false)}
                  className="text-orion-text hover:text-orion-accent py-3 px-3 rounded-lg text-sm font-medium transition-colors duration-200 active:bg-orion-highlight/50"
                >
                  {l.label}
                </Link>
              ))}
              {categories.length > 0 && (
                <div className="border-t border-orion-border/50 mt-2 pt-2">
                  <p className="text-orion-muted text-[10px] font-semibold uppercase tracking-[0.2em] px-3 py-2">
                    Categories
                  </p>
                  {categories.slice(0, 8).map(cat => (
                    <Link
                      key={cat.slug}
                      href={`/collections?cat=${encodeURIComponent(cat.slug)}`}
                      onClick={() => setMobileOpen(false)}
                      className="block text-orion-muted hover:text-orion-accent py-2.5 px-3 rounded-lg text-sm transition-colors duration-200"
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
              )}
              <div className="border-t border-orion-border/50 mt-2 pt-2 space-y-2">
                <Link
                  href="/customer/signup"
                  onClick={() => setMobileOpen(false)}
                  className="w-full text-center"
                >
                  <Button variant="primary" className="w-full" iconLeft={<Lock className="w-3.5 h-3.5" />}>
                    Apply for Wholesale
                  </Button>
                </Link>
                {isAdmin && (
                <Link
                  href="/admin"
                  onClick={() => setMobileOpen(false)}
                  className="text-orion-muted hover:text-orion-accent py-3 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors duration-200"
                >
                  <Lock size={16} /> Admin Dashboard
                </Link>
                )}
                {customer ? (
                  <Link
                    href="/customer/dashboard"
                    onClick={() => setMobileOpen(false)}
                    className="text-orion-text hover:text-orion-accent py-3 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors duration-200"
                  >
                    <User size={16} className="text-orion-accent" /> {customer.studioName || 'Account'}
                  </Link>
                ) : (
                  <Link
                    href="/customer/login"
                    onClick={() => setMobileOpen(false)}
                    className="text-orion-muted hover:text-orion-accent py-3 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors duration-200"
                  >
                    <User size={16} /> Sign In
                  </Link>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  )
}